import { Check, Circle } from "lucide-react";

const PHASES = [
  {
    label: "Evidence",
    title: "Evidence gathering",
    description: "Deepening understanding through concrete examples and outcomes.",
  },
  {
    label: "Narrative",
    title: "Narrative shaping",
    description: "Connecting decisions, patterns, and proof into a coherent direction.",
  },
  {
    label: "Identity",
    title: "Identity compression",
    description: "Distilling the strongest role, focus, and differentiating edge.",
  },
];

export function interviewPhaseForQuestion(questionNumber) {
  if (questionNumber <= 2) return PHASES[0];
  if (questionNumber <= 5) return PHASES[1];
  return PHASES[2];
}

export function sourceStatementFromQuestion(content) {
  if (!content) return "Your answer will become the source context for the next question.";

  const firstParagraph = content.split(/\n\n+/)[0]
    .replace(/\*\*/g, "")
    .replace(/(^|\s)\*([^*]+)\*(?=\s|$)/g, "$1$2")
    .replace(/\s+/g, " ")
    .replace(/^You said,\s*/i, "")
    .trim();

  if (firstParagraph.length <= 220) return firstParagraph;
  return `${firstParagraph.slice(0, 217).trimEnd()}…`;
}

function ProgressStep({ state, title, children }) {
  const Icon = state === "complete" ? Check : Circle;
  return (
    <li className={`context-progress-step ${state}`}>
      <span className="context-progress-icon" aria-hidden="true"><Icon size={12} /></span>
      <div>
        <strong>{title}</strong>
        <p>{children}</p>
      </div>
    </li>
  );
}

export function resumeContextCopy(status) {
  if (status === "ready") return "Ready — parsed résumé facts are available to the interviewer.";
  if (status === "processing") return "Processing — résumé facts are still being prepared.";
  if (status === "failed") return "Unavailable — this interview is using your answers only.";
  return "Not uploaded — this interview is using your answers only.";
}

export default function InterviewContextPanel({ questionNumber, phase, sourceStatement, spec, isBusy, resumeStatus }) {
  return (
    <div className="interview-context-content" data-testid="interview-context-content">
      <section className="context-section context-question" data-testid="question-context-summary">
        <p className="context-label">Question {questionNumber}</p>
        <h2>{phase.label}</h2>
      </section>

      <section className="context-section" data-testid="interview-phase-summary">
        <p className="context-label">Interview phase</p>
        <strong>{phase.title}</strong>
        <p>{phase.description}</p>
      </section>

      <section className="context-section" data-testid="source-statement-summary">
        <p className="context-label">Source statement</p>
        <blockquote>{sourceStatement}</blockquote>
      </section>

      <section className="context-section" data-testid="resume-context-status">
        <p className="context-label">Résumé context</p>
        <p>{resumeContextCopy(resumeStatus)}</p>
      </section>

      <section className="context-section" data-testid="evidence-progress-summary">
        <p className="context-label">Evidence progress</p>
        <ol className="context-progress">
          <ProgressStep state="complete" title="Listening">Capturing the full context of your answer.</ProgressStep>
          <ProgressStep state="active" title={isBusy ? "Connecting" : "Distilling"}>
            {isBusy ? "Connecting your evidence to the next question." : "Identifying signals and core evidence."}
          </ProgressStep>
          <ProgressStep state="upcoming" title="Next question">Preparing the next question for you.</ProgressStep>
        </ol>
      </section>

      <section className="context-section context-profile" data-testid="emerging-profile-summary">
        <p className="context-label">Emerging profile</p>
        <dl>
          <div><dt>Role</dt><dd>{spec.role}</dd></div>
          <div><dt>Focus</dt><dd>{spec.focus}</dd></div>
          <div><dt>Edge</dt><dd>{spec.edge}</dd></div>
        </dl>
      </section>
    </div>
  );
}
